import { useEffect, useRef, type RefObject } from 'react';
import {
  DIRECTOR_FIELDS_REPORT,
  DIRECTOR_FIELDS_STATEMENT,
  DIRECTOR_ORDINALS,
  type DirectorOrdinal,
  normalizeDirectorDisplayName,
  ordinalFromDirectorFieldName,
  sectionFromDirectorFieldName
} from '../utils/directorCrossSectionFields';
import {
  DIRECTOR_CROSS_SUGGEST_BANNER_CLOSE_EVENT,
  openDirectorCrossSuggestBanner,
  setDirectorCrossSuggestBannerContext
} from '../utils/directorCrossSuggestBannerBus';

function readFieldValue(
  formDataRef: RefObject<Record<string, string>>,
  fieldName: string,
  contextRef: string
): string {
  const el = document.querySelector(
    `[name="${fieldName}"][data-context-ref="${contextRef}"]`
  ) as HTMLInputElement | HTMLSelectElement | null;
  const qname = el?.getAttribute('data-qname');
  if (qname && formDataRef.current) {
    const key = `${qname}_${contextRef}`;
    const v = formDataRef.current[key];
    if (v !== undefined && v !== '') {
      return String(v).trim();
    }
  }
  return (el?.value ?? '').trim();
}

function isInsideDirectorSuggestBanner(el: HTMLElement | null): boolean {
  return !!(el && el.closest('[data-director-suggest-banner="true"]'));
}

function isDirectorIdOrTypeField(name: string): boolean {
  if (!name) return false;
  const isId =
    name.includes('IdentificationNumberOf') &&
    name.includes('DirectorWhoSigned') &&
    (name.includes('DirectorsReport') || name.includes('StatementByDirectors'));
  const isType =
    name.includes('TypeOfIdentification') &&
    name.includes('DirectorWhoSigned') &&
    (name.includes('DirectorsReport') || name.includes('StatementByDirectors'));
  return isId || isType;
}

function findFilledSlotInOtherSection(
  formDataRef: RefObject<Record<string, string>>,
  contextRef: string,
  other: 'report' | 'statement',
  normalizedName: string
): { typeVal: string; idVal: string } | null {
  const map = other === 'report' ? DIRECTOR_FIELDS_REPORT : DIRECTOR_FIELDS_STATEMENT;
  for (const ord of DIRECTOR_ORDINALS) {
    const row = map[ord];
    const n = normalizeDirectorDisplayName(readFieldValue(formDataRef, row.name, contextRef));
    if (n !== normalizedName) continue;
    const typeVal = readFieldValue(formDataRef, row.type, contextRef);
    const idVal = readFieldValue(formDataRef, row.id, contextRef);
    if (typeVal && idVal) {
      return { typeVal, idVal };
    }
  }
  return null;
}

const OPEN_DEBOUNCE_MS = 180;

/**
 * Listens for focus on director IC / ID-type fields; opens DOM-controlled banner (no React state).
 * Reads values from formDataRef first, then DOM.
 */
export function useDirectorCrossSectionSuggest(
  enabled: boolean,
  handleFieldChange: (fieldName: string, contextRef: string, value: string, qname?: string) => void,
  formDataRef: RefObject<Record<string, string>>
): void {
  const handleFieldChangeRef = useRef(handleFieldChange);
  handleFieldChangeRef.current = handleFieldChange;

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!enabled) return;

    const tryOpenBanner = (target: HTMLElement) => {
      if (!enabled) return;
      if (isInsideDirectorSuggestBanner(target)) return;
      if (target.getAttribute('data-readonly') === 'true' || (target as HTMLInputElement).readOnly) {
        return;
      }

      const fieldName = (target as HTMLInputElement).name;
      if (!fieldName || !isDirectorIdOrTypeField(fieldName)) {
        return;
      }

      const contextRef = target.getAttribute('data-context-ref');
      if (!contextRef) return;

      const section = sectionFromDirectorFieldName(fieldName);
      const ordinal = ordinalFromDirectorFieldName(fieldName);
      if (!section || !ordinal) return;

      const row =
        section === 'statement'
          ? DIRECTOR_FIELDS_STATEMENT[ordinal]
          : DIRECTOR_FIELDS_REPORT[ordinal];

      const displayName = readFieldValue(formDataRef, row.name, contextRef).trim();
      const nameHere = normalizeDirectorDisplayName(displayName);
      if (!nameHere) return;

      const typeHere = readFieldValue(formDataRef, row.type, contextRef);
      const idHere = readFieldValue(formDataRef, row.id, contextRef);
      if (typeHere && idHere) return;

      const other: 'report' | 'statement' = section === 'report' ? 'statement' : 'report';
      const match = findFilledSlotInOtherSection(formDataRef, contextRef, other, nameHere);
      if (!match) return;

      const targetRow =
        section === 'statement' ? DIRECTOR_FIELDS_STATEMENT[ordinal] : DIRECTOR_FIELDS_REPORT[ordinal];

      const sourceLabel = other === 'report' ? 'Directors Report' : 'Statement by Directors';
      const destLabel = section === 'statement' ? 'Statement by Directors' : 'Directors Report';

      const message = `Director "${displayName}" appears in ${sourceLabel} with ID type and number filled. Copy them into this ${destLabel} row?`;

      const onApply = () => {
        const applyChange = handleFieldChangeRef.current;
        // Always set both from the matched row.
        applyChange(targetRow.type, contextRef, match.typeVal);
        applyChange(targetRow.id, contextRef, match.idVal);

        // Fire native change events so useValidation's lastValueMapRef records the new
        // values. Without this, the map stays '' and when the user later clears the
        // field, blur sees '' === '' and skips validation (no error shown).
        [targetRow.type, targetRow.id].forEach((name) => {
          const el = document.querySelector(
            `[name="${name}"][data-context-ref="${contextRef}"]`
          ) as HTMLElement | null;
          if (el) el.dispatchEvent(new Event('change', { bubbles: true }));
        });
      };

      setDirectorCrossSuggestBannerContext({ message, onApply });
      openDirectorCrossSuggestBanner();
    };

    const onFocusIn = (ev: Event) => {
      const t = ev.target as HTMLElement | null;
      if (!t || (t.tagName !== 'INPUT' && t.tagName !== 'SELECT' && t.tagName !== 'TEXTAREA')) {
        return;
      }

      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }

      debounceRef.current = setTimeout(() => {
        debounceRef.current = null;
        if (document.activeElement !== t) return;
        tryOpenBanner(t);
      }, OPEN_DEBOUNCE_MS);
    };

    const onBannerClose = () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }
    };

    document.addEventListener('focusin', onFocusIn, true);
    document.addEventListener(DIRECTOR_CROSS_SUGGEST_BANNER_CLOSE_EVENT, onBannerClose);
    return () => {
      document.removeEventListener('focusin', onFocusIn, true);
      document.removeEventListener(DIRECTOR_CROSS_SUGGEST_BANNER_CLOSE_EVENT, onBannerClose);
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [enabled, formDataRef]);
}
